import React, { useEffect, useState } from "react";

interface VideoLesson {
    id: number;
    title: string;
    description: string;
    videoUrl: string;
    teacher: string;
    lessonNumber: string;
    thumbnailUrl?: string;
}

const VideoLessons: React.FC = () => {
    const [lessons, setLessons] = useState<VideoLesson[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchLessons = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/videos?populate=*`
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch video lessons");
                }

                const data = await response.json();

                const normalizedLessons: VideoLesson[] = (data?.data || []).map((item: any) => ({
                    id: item.id,
                    title: item.title,
                    description: item.description,
                    videoUrl: item.videoUrl,
                    teacher: item.teacher,
                    lessonNumber: item.lessonNumber,
                }));

                setLessons(normalizedLessons);
            } catch (err) {
                console.error(err);
                setError("Failed to load video lessons.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchLessons();
    }, []);

    const getEmbedUrl = (url: string) => {
        if (!url) return "";
        // Handle standard watch URLs
        if (url.includes("watch?v=")) {
            return url.replace("watch?v=", "embed/");
        }
        // Handle short URLs (youtu.be)
        if (url.includes("youtu.be/")) {
            return url.replace("youtu.be/", "www.youtube.com/embed/");
        }
        // Return as is if already embed or unknown format, assuming it might work or be handled by iframe
        return url;
    };

    if (isLoading) {
        return (
            <div className="flex justify-center py-20">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-mustardYellow border-t-transparent" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="py-20 text-center text-red-500">
                <p>{error}</p>
            </div>
        );
    }

    // If no lessons, show a placeholder or empty state
    if (lessons.length === 0) {
        return (
            <div className="py-20 text-center text-gray-500">
                <p>No video lessons available at the moment.</p>
            </div>
        )
    }

    // The first lesson is the "featured" one (large video)
    const featuredLesson = lessons[0];
    const otherLessons = lessons.slice(1);

    return (
        <div className="container mx-auto px-4 py-16">
            {/* Featured Lesson */}
            {featuredLesson && (
                <div className="mb-16">
                    <div className="aspect-video w-full overflow-hidden rounded-lg bg-gray-200 shadow-lg">
                        <iframe
                            src={getEmbedUrl(featuredLesson.videoUrl)}
                            title={featuredLesson.title}
                            className="h-full w-full"
                            allowFullScreen
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        />
                    </div>
                    <div className="mt-6 flex items-start justify-between">
                        <div>
                            <div className="mb-2 flex items-center gap-2 text-sm text-gray-500">
                                <span>By {featuredLesson.teacher}</span>
                            </div>
                            <h2 className="mb-2 text-2xl font-bold text-darkerGrey">
                                {featuredLesson.title}
                            </h2>
                            <p className="text-gray-600">{featuredLesson.description}</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <span className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-500">
                                {featuredLesson.lessonNumber}
                            </span>
                            {featuredLesson.videoUrl && (
                                <a href={featuredLesson.videoUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm text-mustardYellow hover:underline">
                                    Watch on YouTube ↗
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Grid of other lessons */}
            <div className="grid gap-8 md:grid-cols-2">
                {otherLessons.map((lesson) => (
                    <div key={lesson.id} className="flex flex-col">
                        <div className="aspect-video w-full overflow-hidden rounded-lg bg-gray-200 shadow-md">
                            <iframe
                                src={getEmbedUrl(lesson.videoUrl)}
                                title={lesson.title}
                                className="h-full w-full"
                                allowFullScreen
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            />
                        </div>
                        <div className="mt-4 flex items-start justify-between">
                            <div>
                                <div className="mb-1 text-xs text-gray-500">
                                    By {lesson.teacher}
                                </div>
                                <h3 className="mb-1 text-lg font-bold text-darkerGrey">
                                    {lesson.title}
                                </h3>
                                <p className="text-sm text-gray-600 line-clamp-2">
                                    {lesson.description}
                                </p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                                <span className="rounded border border-gray-300 px-2 py-0.5 text-[10px] text-gray-500">
                                    {lesson.lessonNumber}
                                </span>
                                {lesson.videoUrl && (
                                    <a href={lesson.videoUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-mustardYellow hover:underline">
                                        Watch on YouTube ↗
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default VideoLessons;
